import { useMemo } from "react";
import type {
  BigQueryUsageExecutionSortBy,
  BigQueryUsageFilters,
  BigQueryUsageSortOrder,
  BigQueryUsageTenantKind,
} from "@/features/bigquery-usage/api/bigquery-usage.api";
import {
  eventTimeRangeHours,
  type EventTimeRange,
} from "@/features/events/constants/event-time-ranges";
import { ADMIN_OPS_FALLBACK } from "@/features/admin/lib/admin-ops-settings";
import { useBigQueryUsageQueries } from "@/lib/queries/bigquery-usage.queries";
import { buildServiceByFeature } from "../lib/service-rollup";
import { buildTenantBranding } from "../lib/tenant-branding";
import { resolveBigQueryZeroState } from "../lib/zero-state";
import { BigQueryChart, type BigQueryZoomTarget } from "./BigQueryChart";
import { BigQueryExecutionDrawer } from "./BigQueryExecutionDrawer";
import { BigQueryExecutionsTable } from "./BigQueryExecutionsTable";
import { BigQueryFeatureTable } from "./BigQueryFeatureTable";
import { BigQueryFilterBar } from "./BigQueryFilterBar";
import { BigQueryFingerprintTable } from "./BigQueryFingerprintTable";
import { BigQueryKpiStrip } from "./BigQueryKpiStrip";
import { BigQueryReconciliationDialog } from "./BigQueryReconciliationDialog";
import { BigQueryRetentionNote } from "./BigQueryRetentionNote";
import { BigQueryTenantTable } from "./BigQueryTenantTable";
import { BigQueryZeroStateNotice } from "./BigQueryZeroStateNotice";

/**
 * The tab's whole position, mirrored one-to-one onto the admin route's `bq`
 * search params. It is the single source of truth: clearing a narrowing is a
 * search-param update, never component state, so a position stays pasteable.
 *
 * `range` is the window, shared with the sibling admin tabs rather than given a
 * second vocabulary of its own.
 */
export interface BigQueryUsageViewFilters {
  range: EventTimeRange;
  feature?: string;
  tenantKind?: BigQueryUsageTenantKind;
  tenantId?: string;
  sqlFingerprint?: string;
  cacheHit?: boolean;
  /** Free text over the statement, the job id and the correlation id. */
  search?: string;
  since?: string;
  until?: string;
  sortBy?: BigQueryUsageExecutionSortBy;
  sortOrder?: BigQueryUsageSortOrder;
  /** Which execution's drawer is open. In the URL so a single query is pasteable. */
  rowId?: string;
}

export interface BigQueryUsagePageProps {
  filters: BigQueryUsageViewFilters;
  onFiltersChange: (next: BigQueryUsageViewFilters) => void;
  /**
   * Opens the Events tab on an execution's correlation id. The admin route owns
   * that tab's search shape, so the jump is handed up rather than built here.
   */
  onOpenCorrelation?: (correlationId: string) => void;
  /**
   * `bigQueryRetentionDays` from the operations settings, which the admin route
   * has already read for its sibling tabs. Defaults to what a fresh install
   * shows, so a viewer without the grant sees the same cutoff.
   */
  retentionDays?: number;
}

/** How many statements the breakdown asks for. The API caps this at 100. */
const FINGERPRINT_LIMIT = 25;
/** One page of the executions list. The API caps this at 200 and defaults to 50. */
const EXECUTIONS_PAGE_SIZE = 50;
/**
 * What the list opens on with nothing pinned in the URL: the window's most
 * recent queries, which is what an admin scanning for something unusual expects.
 */
const DEFAULT_EXECUTION_SORT_BY: BigQueryUsageExecutionSortBy = "createdAt";
const DEFAULT_EXECUTION_SORT_ORDER: BigQueryUsageSortOrder = "desc";

/** The window is a scope rather than a narrowing, so it is not one of these. */
function hasNarrowing(filters: BigQueryUsageViewFilters): boolean {
  return (
    filters.feature != null ||
    filters.tenantKind != null ||
    filters.sqlFingerprint != null ||
    filters.cacheHit != null ||
    filters.search != null ||
    filters.since != null
  );
}

function toApiFilters(filters: BigQueryUsageViewFilters): BigQueryUsageFilters {
  return {
    windowHours: eventTimeRangeHours(filters.range),
    feature: filters.feature,
    tenantKind: filters.tenantKind,
    tenantId: filters.tenantId,
    sqlFingerprint: filters.sqlFingerprint,
    cacheHit: filters.cacheHit,
    search: filters.search,
    // A chart-bucket narrowing overrides the window server-side; the window is
    // still sent so the filter-option lists stay scoped to the same period.
    since: filters.since,
    until: filters.until,
  };
}

/**
 * The BigQuery usage tab: zoom levels one to four, from the window's totals down
 * to a feature, a tenant, a statement, and finally one individual query.
 *
 * An orchestrator only. It holds the reads and turns every narrowing the surfaces
 * below report into one search-param update, which is why none of them keeps a
 * filter of its own.
 */
export function BigQueryUsagePage({
  filters,
  onFiltersChange,
  onOpenCorrelation,
  retentionDays = ADMIN_OPS_FALLBACK.bigQueryRetentionDays,
}: BigQueryUsagePageProps) {
  const sortBy = filters.sortBy ?? DEFAULT_EXECUTION_SORT_BY;
  const sortOrder = filters.sortOrder ?? DEFAULT_EXECUTION_SORT_ORDER;

  const { stats, timeseries, fingerprints, features, tenants, monitoring, executions } =
    useBigQueryUsageQueries({
      filters: toApiFilters(filters),
      // The sort travels with the read: the list is one page of many, so the
      // ordering is the server's and a header click is a new request.
      executions: { sortBy, sortOrder, limit: EXECUTIONS_PAGE_SIZE },
      fingerprintLimit: FINGERPRINT_LIMIT,
    });

  const serviceByFeature = useMemo(
    () => buildServiceByFeature(features.data ?? []),
    [features.data],
  );

  // The chart's legend draws tenant logos, and the timeseries carries none, so
  // the branding comes off the totals read that already has one row per tenant.
  const tenantBranding = useMemo(() => buildTenantBranding(stats.data?.byTenant), [stats.data]);

  const executionRows = useMemo(
    () => executions.data?.pages.flatMap((page) => page.items) ?? [],
    [executions.data?.pages],
  );

  /**
   * The open row is looked up among the pages the API already returned for this
   * admin, never fetched by the id in the URL. A pasted id that is not in them —
   * another tenant's, a mistyped one, one paged past — resolves to nothing and
   * the drawer stays closed, because URL state is not authorization.
   */
  const openExecution = executionRows.find((row) => row.id === filters.rowId) ?? null;

  /**
   * R17's two targets arrive here as one shape and leave as one update. A band
   * carries a `narrowing` and lands as the span *and* the dimension together; a
   * tick or the plot area carries only the span. Either way it is a single call,
   * so the tab makes one read rather than two.
   */
  const handleZoom = (target: BigQueryZoomTarget) => {
    const next: BigQueryUsageViewFilters = {
      ...filters,
      since: target.since,
      until: target.until,
    };

    if (target.narrowing?.dimension === "feature") {
      next.feature = target.narrowing.feature;
    } else if (target.narrowing?.dimension === "tenant") {
      next.tenantKind = target.narrowing.kind;
      next.tenantId = target.narrowing.tenantId;
    } else if (target.narrowing?.dimension === "cacheHit") {
      next.cacheHit = target.narrowing.cacheHit;
    }

    onFiltersChange(next);
  };

  /**
   * A new column opens descending — newest, heaviest, slowest first is the
   * question in every one of the three cases — and re-clicking the active column
   * flips it. Either way the change is a search-param update, so the list is
   * re-read from the server rather than reordered where it sits.
   */
  const handleSort = (column: BigQueryUsageExecutionSortBy) => {
    onFiltersChange({
      ...filters,
      sortBy: column,
      sortOrder: sortBy === column && sortOrder === "desc" ? "asc" : "desc",
    });
  };

  const totalBytesBilled = stats.data?.bytesBilled ?? 0;

  /**
   * R19's three states, decided here and named at the top of the tab.
   *
   * The mode comes from `/stats` per KTD21 rather than from the row count beside
   * it, and Cloud Monitoring is passed as it arrived: a read that failed or
   * degraded reports no count, which leaves a genuinely quiet window reading as
   * quiet instead of as a fault.
   */
  const zeroState = resolveBigQueryZeroState({
    telemetryMode: stats.data?.telemetryMode,
    recordedQueries: stats.data?.queries ?? 0,
    monitoring: monitoring.data,
    isNarrowed: hasNarrowing(filters),
  });

  return (
    // Sized to its own content rather than to the tab panel, like the token usage
    // tab: every card here is `shrink-0`, so a height-constrained column would end
    // at the viewport and strand this padding above the cards that overflow past
    // it, leaving the last one flush against the end of the scroll.
    <div className="flex shrink-0 flex-col gap-4 pb-6">
      <BigQueryFilterBar
        filters={filters}
        featureOptions={features.data ?? []}
        tenantOptions={tenants.data ?? []}
        isOptionsLoading={features.isPending || tenants.isPending}
        resultCount={stats.data?.queries ?? null}
        hasActiveFilters={hasNarrowing(filters)}
        onFiltersChange={onFiltersChange}
        // Clear-all preserves the window and the executions list's own sort:
        // neither is a narrowing, and resetting them would move the tab to a
        // period nobody asked about.
        onClearFilters={() =>
          onFiltersChange({
            range: filters.range,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
          })
        }
        onClearRange={() => onFiltersChange({ ...filters, since: undefined, until: undefined })}
        actions={
          // Sits with the filters rather than at the foot of the tab, like the
          // token usage tab's: it is one sentence about whether the figures below
          // add up, and it should be readable before scrolling through them. A
          // Monitoring read that could not be made degrades inside the dialog
          // (R20) — it never takes the tab down and never changes which zero
          // state the notice below reports.
          <BigQueryReconciliationDialog
            monitoring={monitoring.data}
            isLoading={monitoring.isPending}
            recordedQueries={stats.data?.queries ?? 0}
            recordedBytesBilled={totalBytesBilled}
            isNarrowed={hasNarrowing(filters)}
          />
        }
      />

      {/* Above everything it explains. The strip, the chart and the three
          breakdowns all key their empty lines on a row count, which cannot tell
          an environment that records nothing from an hour in which nothing ran —
          so the reason is read before the zeros rather than after them (R19). */}
      {zeroState ? (
        <BigQueryZeroStateNotice state={zeroState} monitoring={monitoring.data} />
      ) : null}

      {/* Also above the figures it explains: the first stretch of a window that
          outruns retention is empty because those rows were deleted (R24). */}
      <BigQueryRetentionNote windowSince={stats.data?.since} retentionDays={retentionDays} />

      <BigQueryKpiStrip
        stats={stats.data}
        isLoading={stats.isLoading}
        isError={stats.isError}
        onRetry={() => void stats.refetch()}
        isStale={stats.isPlaceholderData}
        onSelectUnattributed={() =>
          onFiltersChange({ ...filters, tenantKind: "unattributed", tenantId: undefined })
        }
      />

      <BigQueryChart
        data={timeseries.data}
        isLoading={timeseries.isLoading}
        isError={timeseries.isError}
        onRetry={() => void timeseries.refetch()}
        isPlaceholderData={timeseries.isPlaceholderData}
        serviceByFeature={serviceByFeature}
        tenantBranding={tenantBranding}
        onZoom={handleZoom}
      />

      {/* One question from two sides — which code path spent the bytes, and who
          it spent them for — so the two sit together to be read against each
          other. They pair rather than the statement table because both carry the
          same six measures, which keeps the columns of one aligned with the
          other. `items-start` lets each card end where its rows do instead of
          padding the shorter one out to match its neighbour. */}
      <div className="grid shrink-0 items-start gap-4 xl:grid-cols-2">
        <BigQueryFeatureTable
          rows={stats.data?.byFeature ?? []}
          isLoading={stats.isLoading}
          isError={stats.isError}
          onRetry={() => void stats.refetch()}
          isStale={stats.isPlaceholderData}
          totalBytesBilled={totalBytesBilled}
          serviceByFeature={serviceByFeature}
          selectedFeature={filters.feature}
          onSelectFeature={(feature) =>
            onFiltersChange({
              ...filters,
              feature: filters.feature === feature ? undefined : feature,
            })
          }
        />
        <BigQueryTenantTable
          rows={stats.data?.byTenant ?? []}
          isLoading={stats.isLoading}
          isError={stats.isError}
          onRetry={() => void stats.refetch()}
          isStale={stats.isPlaceholderData}
          totalBytesBilled={totalBytesBilled}
          selected={
            filters.tenantKind == null
              ? undefined
              : { kind: filters.tenantKind, tenantId: filters.tenantId }
          }
          onSelectTenant={(selection) => {
            // One test for both fields. A tenant narrowing is the pair, and the
            // API rejects a named tenant whose kind was cleared without its id.
            const isSelected =
              filters.tenantKind === selection.kind && filters.tenantId === selection.tenantId;
            onFiltersChange({
              ...filters,
              tenantKind: isSelected ? undefined : selection.kind,
              tenantId: isSelected ? undefined : selection.tenantId,
            });
          }}
        />
      </div>

      {/* Its own row: a normalized statement is far wider than a feature name,
          so pairing it with anything would squeeze both. */}
      <BigQueryFingerprintTable
        rows={fingerprints.data ?? []}
        isLoading={fingerprints.isLoading}
        isError={fingerprints.isError}
        onRetry={() => void fingerprints.refetch()}
        isStale={fingerprints.isPlaceholderData}
        totalBytesBilled={totalBytesBilled}
        selectedFingerprint={filters.sqlFingerprint}
        onSelectFingerprint={(sqlFingerprint) =>
          onFiltersChange({
            ...filters,
            sqlFingerprint:
              filters.sqlFingerprint === sqlFingerprint ? undefined : sqlFingerprint,
          })
        }
      />

      {/* Zoom level four. It renders whether or not a narrowing is active: with
          none it is the window's most recent queries, and a drill-in above
          narrows it in place rather than revealing it. */}
      <BigQueryExecutionsTable
        rows={executionRows}
        // Pending rather than loading: the read is held back until the headline
        // query resolves, and a deferred list is not an empty one.
        isLoading={executions.isPending}
        isError={executions.isError}
        onRetry={() => void executions.refetch()}
        isStale={executions.isPlaceholderData}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        hasNextPage={executions.hasNextPage}
        isFetchingNextPage={executions.isFetchingNextPage}
        onLoadMore={() => void executions.fetchNextPage()}
        selectedRowId={filters.rowId ?? null}
        onSelectRow={(row) => onFiltersChange({ ...filters, rowId: row.id })}
      />

      {/* Closing clears the row from the URL and nothing else: zooming out one
          level is not a reset of the narrowing that got here (R32). */}
      <BigQueryExecutionDrawer
        execution={openExecution}
        onClose={() => onFiltersChange({ ...filters, rowId: undefined })}
        onOpenCorrelation={onOpenCorrelation}
      />

    </div>
  );
}
